import { z } from 'zod';
import type { UserRole } from './me';

/**
 * Machines d'états (`user-workflow.md`) sous forme de données pures : statuts, tables de
 * transitions `{ from, to, roles, conditions }` et helpers de lecture. `packages/core/src/state`
 * applique les conditions ; le SPA lit les tables pour n'afficher que les cibles autorisées.
 * Les valeurs sont celles des enums Postgres de `data-model.md`.
 */

export const OPPORTUNITY_STATUSES = [
  'new',
  'triaged',
  'shortlisted',
  'experiment_planned',
  'experiment_running',
  'validated',
  'invalidated',
  'inconclusive',
  'building',
  'launched',
  'rejected',
  'parked',
  'merged',
  'archived',
] as const;
export const OpportunityStatus = z.enum(OPPORTUNITY_STATUSES);
export type OpportunityStatus = z.infer<typeof OpportunityStatus>;

export const EXPERIMENT_STATUSES = ['draft', 'planned', 'running', 'completed', 'aborted'] as const;
export const ExperimentStatus = z.enum(EXPERIMENT_STATUSES);
export type ExperimentStatus = z.infer<typeof ExperimentStatus>;

export const SCAN_STATUSES = [
  'queued',
  'running',
  'paused_budget',
  'completed',
  'failed',
  'cancelled',
] as const;
export const ScanStatus = z.enum(SCAN_STATUSES);
export type ScanStatus = z.infer<typeof ScanStatus>;

export const VERDICTS = ['validated', 'invalidated', 'inconclusive'] as const;
export const Verdict = z.enum(VERDICTS);
export type Verdict = z.infer<typeof Verdict>;

/** Raisons de rejet d'une opportunité (liste fermée, libellés traduits côté UI). */
export const REJECTION_REASONS = [
  'not_a_job',
  'informational',
  'brand',
  'excluded_category',
  'too_small',
  'well_served',
  'not_buildable',
  'duplicate',
  'other',
] as const;
export const RejectionReason = z.enum(REJECTION_REASONS);
export type RejectionReason = z.infer<typeof RejectionReason>;

/**
 * Clés de condition attachées aux transitions. Chaque clé est vérifiée par `packages/core`
 * à partir d'un contexte chargé par le service ; le SPA s'en sert pour afficher les champs requis.
 *
 * - `interpretation_confirmed` : job et segment non vides (`new → triaged`).
 * - `rationale_required` : justification textuelle non vide.
 * - `reason_required` : raison de rejet parmi `REJECTION_REASONS` (+ rationale si `other`).
 * - `merge_target_required` : cible de fusion fournie, différente de soi, ni `merged` ni `archived`.
 * - `no_blocking_unknowns_or_rationale` : aucune inconnue bloquante non résolue, sinon rationale.
 * - `planned_experiment_exists` : une expérience `planned` existe pour l'opportunité.
 * - `experiment_running` : une expérience `running` existe pour l'opportunité.
 * - `verdict_required` : verdict rendu (et cohérent avec le statut cible pour une opportunité).
 * - `launch_date_required` : date de lancement fournie.
 * - `restore_previous_status` : la cible est le statut mémorisé avant `parked`.
 * - `plan_requirements_met` : expérience avec ≥ 1 requête, budget > 0 et seuils complets.
 * - `results_required` : résultats d'expérience saisis.
 */
export const TRANSITION_CONDITIONS = [
  'interpretation_confirmed',
  'rationale_required',
  'reason_required',
  'merge_target_required',
  'no_blocking_unknowns_or_rationale',
  'planned_experiment_exists',
  'experiment_running',
  'verdict_required',
  'launch_date_required',
  'restore_previous_status',
  'plan_requirements_met',
  'results_required',
] as const;
export const TransitionCondition = z.enum(TRANSITION_CONDITIONS);
export type ConditionKey = z.infer<typeof TransitionCondition>;

/** Rôle humain autorisé à déclencher une transition. `[]` = réservée au système (workflow, cron). */
export type TransitionRole = UserRole;

export type Transition<S extends string> = Readonly<{
  from: S;
  to: S;
  roles: readonly TransitionRole[];
  conditions: readonly ConditionKey[];
}>;

const MEMBERS: readonly TransitionRole[] = ['member', 'admin'];
const ADMINS: readonly TransitionRole[] = ['admin'];
const SYSTEM: readonly TransitionRole[] = [];

function transition<S extends string>(
  from: S,
  to: S,
  roles: readonly TransitionRole[],
  conditions: readonly ConditionKey[] = [],
): Transition<S> {
  return { from, to, roles, conditions };
}

function fanIn<S extends string>(
  froms: readonly S[],
  to: S,
  roles: readonly TransitionRole[],
  conditions: readonly ConditionKey[] = [],
): Transition<S>[] {
  return froms.map((from) => transition(from, to, roles, conditions));
}

/** États terminaux d'une opportunité : seul `archived` reste atteignable (sauf réouverture admin). */
export const OPPORTUNITY_TERMINAL_STATUSES = [
  'rejected',
  'invalidated',
  'launched',
  'merged',
  'archived',
] as const satisfies readonly OpportunityStatus[];

/** États non terminaux, hors `parked` : ce sont les seuls que `parked` peut mémoriser et restaurer. */
export const OPPORTUNITY_PARKABLE_STATUSES = [
  'new',
  'triaged',
  'shortlisted',
  'experiment_planned',
  'experiment_running',
  'validated',
  'inconclusive',
  'building',
] as const satisfies readonly OpportunityStatus[];

/** Tableau « Transitions autorisées » de `user-workflow.md`, ligne par ligne. */
export const OPPORTUNITY_TRANSITIONS: readonly Transition<OpportunityStatus>[] = [
  transition('new', 'triaged', MEMBERS, ['interpretation_confirmed']),
  ...fanIn(['new', 'triaged', 'shortlisted', 'experiment_planned'], 'rejected', MEMBERS, [
    'reason_required',
  ]),
  ...fanIn(OPPORTUNITY_PARKABLE_STATUSES, 'parked', MEMBERS),
  ...fanIn(['new', 'triaged', 'shortlisted'], 'merged', MEMBERS, ['merge_target_required']),
  transition('triaged', 'shortlisted', MEMBERS, ['no_blocking_unknowns_or_rationale']),
  transition('shortlisted', 'experiment_planned', MEMBERS, ['planned_experiment_exists']),
  transition('experiment_planned', 'experiment_running', MEMBERS, ['experiment_running']),
  transition('experiment_running', 'validated', MEMBERS, [
    'verdict_required',
    'rationale_required',
  ]),
  transition('experiment_running', 'invalidated', MEMBERS, [
    'verdict_required',
    'rationale_required',
  ]),
  transition('experiment_running', 'inconclusive', MEMBERS, [
    'verdict_required',
    'rationale_required',
  ]),
  transition('inconclusive', 'shortlisted', MEMBERS),
  // Abandon d'expérience (T26) : rollback posé par le serveur, jamais proposé à un humain.
  ...fanIn<OpportunityStatus>(['experiment_planned', 'experiment_running'], 'shortlisted', SYSTEM),
  transition('validated', 'building', MEMBERS),
  transition('building', 'launched', MEMBERS, ['launch_date_required']),
  ...fanIn(['rejected', 'invalidated', 'launched', 'merged'], 'archived', MEMBERS),
  transition('rejected', 'new', ADMINS, ['rationale_required']),
  // Retour de `parked` : uniquement vers le statut mémorisé (`previous_status`).
  ...OPPORTUNITY_PARKABLE_STATUSES.map((to) =>
    transition<OpportunityStatus>('parked', to, MEMBERS, ['restore_previous_status']),
  ),
];

/** `draft → planned → running → completed`, `aborted` depuis tout état non terminal. */
export const EXPERIMENT_TRANSITIONS: readonly Transition<ExperimentStatus>[] = [
  transition('draft', 'planned', MEMBERS, ['plan_requirements_met']),
  transition('planned', 'running', MEMBERS),
  transition('running', 'completed', MEMBERS, ['results_required', 'verdict_required']),
  ...fanIn(['draft', 'planned', 'running'], 'aborted', MEMBERS, ['rationale_required']),
];

/**
 * Cycle de vie d'un scan. Les transitions pilotées par le Workflow ont `roles: []` ; l'annulation
 * est ouverte aux membres, l'approbation de budget (`paused_budget → running`) aux admins ; la
 * relance d'un scan `failed` (`retryScan`, T10) le remet en file.
 */
export const SCAN_TRANSITIONS: readonly Transition<ScanStatus>[] = [
  transition('queued', 'running', SYSTEM),
  transition('queued', 'cancelled', MEMBERS),
  transition('running', 'completed', SYSTEM),
  transition('running', 'paused_budget', SYSTEM),
  transition('running', 'failed', SYSTEM),
  transition('running', 'cancelled', MEMBERS),
  transition('paused_budget', 'running', ADMINS),
  transition('paused_budget', 'cancelled', ADMINS),
  transition('paused_budget', 'failed', SYSTEM),
  transition('failed', 'queued', MEMBERS),
];

/** Ligne de table pour un couple `from → to`, ou `undefined` si la transition n'existe pas. */
export function findTransition<S extends string>(
  table: readonly Transition<S>[],
  from: S,
  to: S,
): Transition<S> | undefined {
  return table.find((t) => t.from === from && t.to === to);
}

/** Cibles atteignables depuis `from` par `role` dans une table (dédupliquées, ordre de la table). */
export function transitionTargets<S extends string>(
  table: readonly Transition<S>[],
  from: S,
  role: TransitionRole,
): S[] {
  const targets: S[] = [];
  for (const t of table) {
    if (t.from === from && t.roles.includes(role) && !targets.includes(t.to)) targets.push(t.to);
  }
  return targets;
}

/** Cibles d'une opportunité depuis `from` pour `role`, conditions non évaluées (rôle du SPA). */
export function allowedTargets(from: OpportunityStatus, role: TransitionRole): OpportunityStatus[] {
  return transitionTargets(OPPORTUNITY_TRANSITIONS, from, role);
}
